import { useEffect, useState } from "react";
import { api, errMsg, PRIORITIES } from "@/lib/api";
import { PageHeader, CheckList } from "@/components/common";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Card } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { toast } from "sonner";
import { ImportDialog } from "@/components/ImportDialog";
import { ListActions } from "@/components/ListActions";
import { Plus, Pencil, Layers, Users, Clock } from "lucide-react";

const EMPTY = { name: "", description: "", sector_id: null, user_ids: [], supervisor_ids: [], os_type_ids: [], priority: "Normal",
  sla_hours: { Baixa: 48, Normal: 24, Alta: 4, Urgente: 2 }, business_hours: { start: "08:00", end: "18:00" }, status: "Ativa", distribution: "manual", channel_ids: [] };
const DIST = { manual: "Manual (supervisor escolhe)", round_robin: "Automática — rodízio", least_load: "Automática — menor carga" };

export default function Queues() {
  const [rows, setRows] = useState([]);
  const [users, setUsers] = useState([]);
  const [sectors, setSectors] = useState([]);
  const [types, setTypes] = useState([]);
  const [open, setOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState(EMPTY);

  const load = () => api.get("/queues").then((r) => setRows(r.data));
  useEffect(() => {
    load();
    api.get("/users").then((r) => setUsers(r.data));
    api.get("/sectors").then((r) => setSectors(r.data));
    api.get("/os-types").then((r) => setTypes(r.data));
  }, []);

  const openCreate = () => { setEditing(null); setForm(EMPTY); setOpen(true); };
  const openEdit = (q) => { setEditing(q); setForm({ ...EMPTY, ...q }); setOpen(true); };
  const set = (k) => (v) => setForm({ ...form, [k]: v?.target ? v.target.value : v });

  const submit = async () => {
    if (!form.name) return toast.error("Nome é obrigatório");
    const payload = { ...form };
    ["id", "tenant_id", "created_at", "sector_name", "user_names", "supervisor_names", "open_count", "rr_index"].forEach((k) => delete payload[k]);
    payload.sla_hours = Object.fromEntries(Object.entries(payload.sla_hours).map(([k, v]) => [k, Number(v) || 0]));
    try {
      if (editing) { await api.put(`/queues/${editing.id}`, payload); toast.success("Fila atualizada"); }
      else { await api.post("/queues", payload); toast.success("Fila criada"); }
      setOpen(false); load();
    } catch (e) { toast.error(errMsg(e)); }
  };

  const toggleActive = (q, active) => {
    const payload = { ...q, status: active ? "Ativa" : "Inativa" };
    ["id", "tenant_id", "created_at", "sector_name", "user_names", "supervisor_names", "open_count", "rr_index", "order"].forEach((k) => delete payload[k]);
    return api.put(`/queues/${q.id}`, payload);
  };

  const agents = users.filter((u) => u.role !== "TENANT_ADMIN");
  const supervisors = users.filter((u) => u.role === "SUPERVISOR" || u.role === "TENANT_ADMIN");

  return (
    <div className="p-4 sm:p-6 lg:p-8 space-y-5 sm:space-y-6">
      <PageHeader title="Filas" subtitle="Filas de atendimento com usuários, supervisores, tipos de OS, SLA e regra de distribuição.">
        <ImportDialog resource="queues" label="fila" onDone={load} />
        <Button onClick={openCreate} className="bg-emerald-600 hover:bg-emerald-700" data-testid="create-queue-btn"><Plus className="w-4 h-4 mr-2" /> Nova fila</Button>
      </PageHeader>

      <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-4">
        {rows.map((q, i) => (
          <Card key={q.id} className={`p-5 border-slate-200 dark:border-slate-800 hover:shadow-md transition-shadow ${q.status === "Ativa" ? "" : "opacity-60"}`} data-testid={`queue-card-${q.name}`}>
            <div className="flex items-start justify-between">
              <div className="flex items-center gap-3">
                <div className="w-10 h-10 rounded-xl bg-emerald-500/10 flex items-center justify-center"><Layers className="w-5 h-5 text-emerald-600" /></div>
                <div>
                  <h3 className="font-bold text-slate-900 dark:text-white">{q.name}</h3>
                  <p className="text-xs text-slate-500">{q.sector_name || "Sem setor"} · {DIST[q.distribution]?.split(" (")[0]}</p>
                </div>
              </div>
              <Badge variant="outline" className={q.status === "Ativa" ? "text-emerald-600 border-emerald-500/30" : "text-slate-500"}>{q.status}</Badge>
            </div>
            <div className="mt-4 grid grid-cols-3 gap-2 text-center">
              <div className="rounded-lg bg-slate-50 dark:bg-slate-800 p-2"><p className="text-lg font-extrabold text-amber-600">{q.open_count}</p><p className="text-[10px] uppercase text-slate-500">Abertas</p></div>
              <div className="rounded-lg bg-slate-50 dark:bg-slate-800 p-2"><p className="text-lg font-extrabold text-slate-900 dark:text-white">{q.user_ids.length}</p><p className="text-[10px] uppercase text-slate-500">Atendentes</p></div>
              <div className="rounded-lg bg-slate-50 dark:bg-slate-800 p-2"><p className="text-lg font-extrabold text-slate-900 dark:text-white">{q.sla_hours?.Normal}h</p><p className="text-[10px] uppercase text-slate-500">SLA normal</p></div>
            </div>
            <div className="mt-3 text-xs text-slate-600 dark:text-slate-400 space-y-1">
              <p className="flex items-center gap-1.5"><Users className="w-3.5 h-3.5" /> {q.user_names.length ? q.user_names.join(", ") : "Nenhum atendente"}</p>
              <p className="flex items-center gap-1.5"><Clock className="w-3.5 h-3.5" /> {q.business_hours?.start} – {q.business_hours?.end}</p>
            </div>
            <div className="flex gap-2 mt-4 items-center flex-wrap">
              <Button variant="outline" size="sm" onClick={() => openEdit(q)} className="flex-1" data-testid={`edit-queue-${q.name}`}><Pencil className="w-3.5 h-3.5 mr-1" /> Configurar</Button>
              <ListActions item={q} index={i} rows={rows} resource="queues" isActive={q.status === "Ativa"} onToggle={toggleActive} onChanged={load} testKey={`queue-${q.name}`} />
            </div>
          </Card>
        ))}
      </div>

      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="bg-white dark:bg-slate-900 sm:max-w-3xl max-h-[90vh] overflow-y-auto">
          <DialogHeader><DialogTitle>{editing ? `Configurar fila — ${editing.name}` : "Nova fila"}</DialogTitle></DialogHeader>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            <div className="space-y-3">
              <p className="text-xs font-bold uppercase tracking-wider text-slate-500">Informações</p>
              <div><Label>Nome *</Label><Input data-testid="queue-name-input" value={form.name} onChange={set("name")} /></div>
              <div><Label>Descrição</Label><Input value={form.description} onChange={set("description")} /></div>
              <div><Label>Setor</Label>
                <Select value={form.sector_id || "none"} onValueChange={(v) => set("sector_id")(v === "none" ? null : v)}>
                  <SelectTrigger data-testid="queue-sector-select"><SelectValue /></SelectTrigger>
                  <SelectContent className="bg-white dark:bg-slate-900"><SelectItem value="none">Sem setor</SelectItem>{sectors.map((s) => <SelectItem key={s.id} value={s.id}>{s.name}</SelectItem>)}</SelectContent>
                </Select></div>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                <div><Label>Prioridade padrão</Label>
                  <Select value={form.priority} onValueChange={set("priority")}>
                    <SelectTrigger><SelectValue /></SelectTrigger>
                    <SelectContent className="bg-white dark:bg-slate-900">{PRIORITIES.map((p) => <SelectItem key={p} value={p}>{p}</SelectItem>)}</SelectContent>
                  </Select></div>
                <div><Label>Status</Label>
                  <Select value={form.status} onValueChange={set("status")}>
                    <SelectTrigger><SelectValue /></SelectTrigger>
                    <SelectContent className="bg-white dark:bg-slate-900"><SelectItem value="Ativa">Ativa</SelectItem><SelectItem value="Inativa">Inativa</SelectItem></SelectContent>
                  </Select></div>
              </div>
              <div><Label>Distribuição</Label>
                <Select value={form.distribution} onValueChange={set("distribution")}>
                  <SelectTrigger data-testid="queue-distribution-select"><SelectValue /></SelectTrigger>
                  <SelectContent className="bg-white dark:bg-slate-900">{Object.entries(DIST).map(([k, v]) => <SelectItem key={k} value={k}>{v}</SelectItem>)}</SelectContent>
                </Select></div>
              <p className="text-xs font-bold uppercase tracking-wider text-slate-500 pt-2">SLA (horas)</p>
              <div className="grid grid-cols-2 sm:grid-cols-4 gap-2">
                {PRIORITIES.map((p) => (
                  <div key={p}><Label className="text-xs">{p}</Label><Input type="number" min={1} data-testid={`queue-sla-${p}`} value={form.sla_hours[p]} onChange={(e) => setForm({ ...form, sla_hours: { ...form.sla_hours, [p]: e.target.value } })} /></div>
                ))}
              </div>
              <p className="text-xs font-bold uppercase tracking-wider text-slate-500 pt-2">Horário de atendimento</p>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                <div><Label className="text-xs">Início</Label><Input type="time" value={form.business_hours.start} onChange={(e) => setForm({ ...form, business_hours: { ...form.business_hours, start: e.target.value } })} /></div>
                <div><Label className="text-xs">Fim</Label><Input type="time" value={form.business_hours.end} onChange={(e) => setForm({ ...form, business_hours: { ...form.business_hours, end: e.target.value } })} /></div>
              </div>
            </div>
            <div className="space-y-4">
              <div><p className="text-xs font-bold uppercase tracking-wider text-slate-500 mb-1">Usuários da fila</p>
                <CheckList items={agents.map((u) => ({ id: u.id, label: u.name }))} value={form.user_ids} onChange={set("user_ids")} testPrefix="queue-user" /></div>
              <div><p className="text-xs font-bold uppercase tracking-wider text-slate-500 mb-1">Supervisores</p>
                <CheckList items={supervisors.map((u) => ({ id: u.id, label: u.name }))} value={form.supervisor_ids} onChange={set("supervisor_ids")} testPrefix="queue-sup" /></div>
              <div><p className="text-xs font-bold uppercase tracking-wider text-slate-500 mb-1">Tipos de OS permitidos</p>
                <CheckList items={types.map((t) => ({ id: t.id, label: t.name }))} value={form.os_type_ids} onChange={set("os_type_ids")} testPrefix="queue-type" /></div>
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setOpen(false)}>Cancelar</Button>
            <Button onClick={submit} className="bg-emerald-600 hover:bg-emerald-700" data-testid="save-queue-btn">Salvar</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
